import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useChapters } from '../../hooks/useChapters';
import AppIcon from '../ui/AppIcon';
import { FaLock, FaBook, FaDiamond, FaCheck, FaXmark, FaCircleInfo } from 'react-icons/fa6';

const ChaptersPage = () => {
  const { user } = useAuth();
  const { getVisibleChapters, loading } = useChapters();
  const basicChapters = getVisibleChapters('basic');
  const advancedChapters = user ? getVisibleChapters('advanced') : [];

  const advancedSectionRef = useRef(null);

  const [expandedId, setExpandedId] = useState(null);
  const [showExercises, setShowExercises] = useState({});
  const [userAnswers, setUserAnswers] = useState({});
  const [submittedAnswers, setSubmittedAnswers] = useState({});

  const toggleChapter = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const toggleExercise = (chapterId) => {
    setShowExercises(prev => ({ ...prev, [chapterId]: !prev[chapterId] }));
  };

  const handleSelectOption = (exerciseId, option) => {
    if (submittedAnswers[exerciseId]) return;
    setUserAnswers(prev => ({ ...prev, [exerciseId]: option }));
  };

  const handleSubmit = (exerciseId, exercise) => {
    const answer = userAnswers[exerciseId];
    if (!answer) return;
    const isCorrect = answer === exercise.answer;
    setSubmittedAnswers(prev => ({
      ...prev,
      [exerciseId]: { isCorrect, correctAnswer: exercise.answer, userAnswer: answer, explanation: exercise.explanation }
    }));
  };

  const scrollToAdvanced = () => {
    advancedSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const renderLoginGate = (message) => (
    <div className="access-gate">
      <div className="access-gate-icon"><FaLock /></div>
      <div className="access-gate-title">Login Required</div>
      <div className="access-gate-text">{message}</div>
      <div className="access-gate-actions">
        <Link to="/login" className="btn btn-primary">Login</Link>
        <Link to="/signup" className="btn btn-outline">Sign Up</Link>
      </div>
    </div>
  );

  const renderOption = (exercise, option, index) => {
    const submitted = submittedAnswers[exercise.id];
    const isSelected = userAnswers[exercise.id] === option;
    const isCorrectAnswer = option === exercise.answer;
    const isUserAnswer = submitted && submitted.userAnswer === option;

    let className = 'exercise-option';
    if (submitted) {
      if (isCorrectAnswer) className += ' correct';
      else if (isUserAnswer) className += ' incorrect';
    } else if (isSelected) {
      className += ' selected';
    }

    return (
      <button
        key={index}
        className={className}
        onClick={() => handleSelectOption(exercise.id, option)}
        disabled={!!submitted}
      >
        <span className="exercise-option-text">{option}</span>
        {submitted && isCorrectAnswer && <FaCheck className="option-icon correct-icon" />}
        {submitted && isUserAnswer && !isCorrectAnswer && <FaXmark className="option-icon incorrect-icon" />}
      </button>
    );
  };

  const renderExercise = (exercise, index) => {
    const submitted = submittedAnswers[exercise.id];

    return (
      <div key={exercise.id} className="exercise-item">
        <div className="exercise-header">
          <span className="exercise-number">Question {index + 1}</span>
          {submitted && (
            <span className={`result-badge ${submitted.isCorrect ? 'correct' : 'incorrect'}`}>
              {submitted.isCorrect ? <FaCheck size={12} /> : <FaXmark size={12} />}
              {submitted.isCorrect ? ' Correct' : ' Incorrect'}
            </span>
          )}
        </div>
        <p className="exercise-question">{exercise.question}</p>

        {exercise.type === 'multiple-choice' && exercise.options && (
          <div className="exercise-options">
            {exercise.options.map((opt, i) => renderOption(exercise, opt, i))}
          </div>
        )}

        {exercise.type === 'text' && (
          <div className="exercise-text-input">
            <input
              type="text"
              className="form-input"
              placeholder="Enter your answer..."
              value={userAnswers[exercise.id] || ''}
              onChange={(e) => handleSelectOption(exercise.id, e.target.value)}
              disabled={!!submittedAnswers[exercise.id]}
            />
          </div>
        )}

        {!submitted ? (
          <button
            className={`btn btn-secondary exercise-submit${!userAnswers[exercise.id] ? ' disabled' : ''}`}
            onClick={() => handleSubmit(exercise.id, exercise)}
            disabled={!userAnswers[exercise.id]}
          >
            Submit Answer
          </button>
        ) : (
          submitted.explanation && (
            <div className="exercise-explanation">
              <FaCircleInfo className="explanation-icon" />
              <span>{submitted.explanation}</span>
            </div>
          )
        )}
      </div>
    );
  };

  const renderChapterCard = (chapter, badge) => {
    const isExpanded = expandedId === chapter.id;
    const hasExercises = chapter.exercises && chapter.exercises.length > 0;
    const isAdvanced = badge === 'ADVANCED';

    return (
      <div
        key={chapter.id}
        className={`chapter-card${isExpanded ? ' expanded' : ''}`}
        style={isAdvanced ? { borderLeft: '4px solid #FFD700' } : undefined}
      >
        <div className="chapter-header clickable" onClick={() => toggleChapter(chapter.id)}>
          <div>
            <h3>{chapter.title}</h3>
            <div className="chapter-meta">
              <span className="chapter-order">Chapter {chapter.order}</span>
              <span
                className={`chapter-type ${isAdvanced ? 'advanced' : 'basic'}`}
                style={isAdvanced
                  ? { background: '#FFD700', color: '#333' }
                  : { background: '#28a745', color: '#fff' }}
              >
                {badge}
              </span>
            </div>
          </div>
          <span className={`expand-arrow${isExpanded ? ' open' : ''}`}>&#9662;</span>
        </div>

        <p className="chapter-description">{chapter.description}</p>

        {isExpanded && (
          <div className="chapter-expanded-content fade-in">
            {chapter.content && (
              <div className="chapter-content">
                <h4><FaBook style={{ marginRight: 8 }} /> Overview</h4>
                <p>{chapter.content}</p>
              </div>
            )}

            {chapter.advancedContent && (
              user ? (
                <div className="premium-content">
                  <h4><FaDiamond style={{ color: '#FFD700', marginRight: 8 }} /> Detailed Content</h4>
                  <p>{chapter.advancedContent}</p>
                </div>
              ) : (
                renderLoginGate('Login to view the detailed content and expert insights.')
              )
            )}

            {hasExercises && (
              user ? (
                <div className="exercises-content">
                  <h4>
                    <FaCheck style={{ color: '#28a745', marginRight: 8 }} />
                    Interactive Exercises
                    <span className="exercise-count-badge">
                      {chapter.exercises.length} {chapter.exercises.length === 1 ? 'Question' : 'Questions'}
                    </span>
                  </h4>
                  {!showExercises[chapter.id] ? (
                    <button className="btn btn-secondary" onClick={() => toggleExercise(chapter.id)}>
                      Take Exercise
                    </button>
                  ) : (
                    <div className="exercises-list">
                      {chapter.exercises.map((ex, i) => renderExercise(ex, i))}
                    </div>
                  )}
                </div>
              ) : (
                renderLoginGate('Login to access interactive exercises and test your knowledge.')
              )
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          Loading chapters...
        </div>
      </div>
    );
  }

  return (
    <div className="chapters-page">
      <div className="container">
        <div className="chapters-header">
          <AppIcon size={60} />
          <h1>Mindfulness Chapters</h1>
          <p>Explore your journey of self-discovery and inner growth</p>
        </div>

        {/* Section 1: Basic Experiences */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '32px 0 16px' }}>
          <h2 style={{ margin: 0 }}>Basic Experiences</h2>
          <span style={{
            background: '#28a745',
            color: '#fff',
            padding: '2px 10px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5
          }}>
            FREE
          </span>
        </div>

        <div className="chapters-list">
          {basicChapters.map((chapter) => renderChapterCard(chapter, 'BASIC'))}
        </div>

        {/* Section 2: Advanced Experiences */}
        <div ref={advancedSectionRef} style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '48px 0 16px' }}>
          <h2 style={{ margin: 0 }}>Advanced Experiences</h2>
          <span style={{
            background: user ? '#FFD700' : '#dc3545',
            color: user ? '#333' : '#fff',
            padding: '2px 10px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5
          }}>
            {user ? 'UNLOCKED' : 'LOGIN REQUIRED'}
          </span>
        </div>

        {user ? (
          <div className="chapters-list">
            {advancedChapters.length > 0 ? (
              advancedChapters.map((chapter) => renderChapterCard(chapter, 'ADVANCED'))
            ) : (
              <div className="empty-state">
                <p>No advanced chapters available yet.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="access-gate" style={{ borderLeft: '4px solid #FFD700' }}>
            <div className="access-gate-icon"><FaLock /></div>
            <div className="access-gate-title">Login Required</div>
            <div className="access-gate-text">
              Login to access advanced experiences with in-depth content, detailed insights, and interactive exercises.
            </div>
            <div className="access-gate-actions">
              <Link to="/login" className="btn btn-primary">Login</Link>
              <Link to="/signup" className="btn btn-outline">Sign Up</Link>
            </div>
          </div>
        )}

        {basicChapters.length === 0 && advancedChapters.length === 0 && !loading && (
          <div className="empty-state">
            <AppIcon size={80} />
            <h2>No Chapters Available</h2>
            <p>Chapters will appear here once they're added by an admin.</p>
          </div>
        )}

        {/* Upgrade / CTA Section */}
        <div style={{
          textAlign: 'center',
          padding: '48px 24px',
          marginTop: 48,
          borderTop: '1px solid #eee'
        }}>
          <AppIcon size={64} />
          <h2 style={{ marginTop: 16 }}>Want More Experiences?</h2>
          {user ? (
            <button className="btn btn-primary" onClick={scrollToAdvanced}>
              View Advanced Experiences
            </button>
          ) : (
            <Link to="/login" className="btn btn-primary">
              Login to Access Advanced Experiences
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChaptersPage;
